/**
 * Iter 68 — Project Team Picker.
 *
 * Multi-row team selector for the Project create/edit modal.
 * Each row = { emp_id, name, role, designation?, department? }.
 *
 * Reuses <EmployeePicker> for member search + a role select on the right
 * and "+ Add" / chip-remove UX.
 *
 * Props:
 *   value      — array of team member objects (see shape above)
 *   onChange   — callback(newArray)
 */
import React, { useState } from 'react';
import { X, UserPlus, Users } from 'lucide-react';
import EmployeePicker from './EmployeePicker';

const DEFAULT_ROLES = [
  'Project Manager',
  'Project Coordinator',
  'Planning Engineer',
  'Site Engineer',
  'Site Supervisor',
  'QA / QC Engineer',
  'HSE Officer',
  'Procurement Engineer',
  'Billing Engineer',
  'Document Controller',
  'Surveyor',
  'Store In-charge',
];

export default function ProjectTeamPicker({ value = [], onChange }) {
  const members = Array.isArray(value) ? value : [];
  const [pickerEmployee, setPickerEmployee] = useState(null); // full employee object
  const [pickerName, setPickerName] = useState('');
  const [pickerRole, setPickerRole] = useState(DEFAULT_ROLES[3]); // Site Engineer default

  const add = () => {
    const name = (pickerName || '').trim();
    if (!name) return;
    if (members.some((m) => (m.name || '').toLowerCase() === name.toLowerCase() && m.role === pickerRole)) {
      setPickerName('');
      return;
    }
    // Store emp_id + employee_uuid for BUILD 1 project-team resolver
    onChange?.([...members, {
      name,
      role: pickerRole,
      emp_id: pickerEmployee?.emp_id || null,
      employee_uuid: pickerEmployee?.id || null,
      designation: pickerEmployee?.designation || null,
      department: pickerEmployee?.department || null,
    }]);
    setPickerName('');
    setPickerEmployee(null);
  };

  const removeAt = (idx) => {
    const next = members.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  return (
    <div className="space-y-2" data-testid="project-team-picker">
      <div className="grid grid-cols-12 gap-2 items-start">
        <div className="col-span-7">
          <EmployeePicker
            value={pickerName}
            onChange={(name, empId, empObj) => { setPickerName(name); if (empObj) setPickerEmployee(empObj); }}
            placeholder="Search team member by name or emp ID…"
            allowFreeText
            testid="project-team-emp"
          />
        </div>
        <div className="col-span-3">
          <select
            value={pickerRole}
            onChange={(e) => setPickerRole(e.target.value)}
            className="w-full px-2.5 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#3b4872] bg-white"
            data-testid="project-team-role"
          >
            {DEFAULT_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <button
            type="button"
            onClick={add}
            disabled={!pickerName.trim()}
            className="w-full flex items-center justify-center gap-1 px-2.5 py-2 bg-[#3b4872] text-white text-xs font-display font-bold uppercase tracking-wider rounded-md hover:bg-[#2c3658] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="project-team-add"
          >
            <UserPlus size={13} /> Add
          </button>
        </div>
      </div>

      {members.length > 0 ? (
        <ul className="space-y-1.5 max-h-44 overflow-y-auto pr-1" data-testid="project-team-list">
          {members.map((m, idx) => (
            <li
              key={`${m.name}-${m.role}-${idx}`}
              className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5"
              data-testid={`project-team-member-${idx}`}
            >
              <Users size={12} className="text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{m.name}</div>
                {(m.designation || m.department) && (
                  <div className="text-[10px] text-slate-500 truncate">
                    {[m.designation, m.department].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              <span className="text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 bg-indigo-50 text-[#3b4872] rounded-sm whitespace-nowrap">
                {m.role}
              </span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="text-slate-400 hover:text-rose-600 shrink-0"
                title="Remove from team"
                data-testid={`project-team-remove-${idx}`}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[11px] text-slate-400 italic">No team members added yet. Pick someone above and click Add.</p>
      )}
    </div>
  );
}
